import { captureException } from '@sentry/react'
import { Button, OTPInput, Typography, useTheme } from '@themenu/design-system'
import { type FC, type ReactNode, useCallback, useEffect, useId, useState } from 'react'
import { useIntl } from 'react-intl'

import { SecondaryFactor } from '../../../types'
import type { BrActivationChannel, ObfuscatedRecipients } from '../../../types/brActivation'
import { environmentVariables } from '../../../utils/environmentVariables'
import { CodeState } from '../../../hooks/useOtpVerification'
import { ActivationStepLayout } from './ActivationStepLayout'

const COUNTDOWN_DURATION = Number.parseInt(environmentVariables('COUNTDOWN_DURATION'), 10)

/**
 * Single parametrizable OTP step for ALL THREE BR activation usages (contract
 * §6 steps 3/6/7):
 *  - activation MFA: multi-channel, full resend + channel switch,
 *  - phone verification: SMS only, resend allowed, no switch,
 *  - email verification: email only, resend allowed, no switch.
 *
 * The resend state machine (SENT → NOT_RECEIVED → RESEND_AVAILABLE → RESENT →
 * ERROR + countdown) is lifted from the shared login `MfaForm` so we keep a
 * single behaviour — `MfaForm` itself is left untouched (it is shared login code).
 */

export type ActivationOtpFormProps = {
  /** Number of digits — 6 for MFA/email, the contract uses 6 for codes. */
  length: 4 | 6
  /** OTP display variant — masked for the activation PIN-like flows, text for codes. */
  variant?: 'text' | 'password'
  /** Channel currently used to deliver the code. */
  channel: BrActivationChannel
  /**
   * Alternative channels the user may switch to. The switch UI renders ONLY
   * when this contains a channel other than `channel` (fixes the POC pitfall
   * where the switch showed up with no real alternative).
   */
  channels?: BrActivationChannel[]
  /** Masked recipient shown in bold in the message (already masked server-side). */
  maskedRecipient?: string
  /**
   * Per-channel masked targets (already masked server-side). When provided (MFA
   * usage), the recipient shown in the message follows the ACTIVE channel after a
   * switch — fixes the bug where it stayed pinned to the initial channel. The
   * single-channel usages keep using the fixed `maskedRecipient` instead.
   */
  obfuscatedRecipients?: ObfuscatedRecipients
  /** i18n id of the title (accepts a blue <strong> accent). */
  titleId: string
  /** i18n id of the descriptive message; receives `recipient` + `channel`. */
  messageId: string
  /**
   * Submit the entered code. Receives the currently ACTIVE channel so MFA
   * verification stays consistent after a channel switch (fixes the POC pitfall
   * where the code was always verified against the initial channel). Single-channel
   * usages can ignore the channel argument. Reject with InvalidCodeError to
   * surface a generic error.
   */
  onSubmit: (code: string, channel: BrActivationChannel) => Promise<void>
  /** (Re)send a code on the given channel. */
  onResend: (channel: BrActivationChannel) => Promise<void>
  /** Optional generic error already surfaced (e.g. wrong code). */
  errorMessageId?: string | null
  /** Optional action slot rendered inside the error Alert (e.g. a care link). */
  errorActions?: ReactNode
  /** Whether a back button is shown. */
  showBackButton?: boolean
  onBack?: () => void
  countdownDuration?: number
}

export const ActivationOtpForm: FC<ActivationOtpFormProps> = ({
  length,
  variant = 'text',
  channel,
  channels,
  maskedRecipient,
  obfuscatedRecipients,
  titleId,
  messageId,
  onSubmit,
  onResend,
  errorMessageId = null,
  errorActions,
  showBackButton = false,
  onBack,
  countdownDuration = COUNTDOWN_DURATION,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const errorId = useId()
  const otpDescriptionId = useId()

  const [otpValue, setOtpValue] = useState('')
  const [activeChannel, setActiveChannel] = useState<BrActivationChannel>(channel)
  const [countDownValue, setCountDownValue] = useState(countdownDuration)
  const [codeState, setCodeState] = useState<CodeState>(CodeState.SENT)
  const [submitting, setSubmitting] = useState(false)

  // Multi-channel (MFA) usage is discriminated by the presence of `channels`:
  // the single-channel phone/email verify usages pass none.
  const isMultiChannel = (channels?.length ?? 0) > 0

  const handleResend = useCallback(
    async (nextChannel: BrActivationChannel) => {
      setCountDownValue(countdownDuration)
      setCodeState(CodeState.RESENT)
      setActiveChannel(nextChannel)
      try {
        await onResend(nextChannel)
      } catch (error) {
        captureException(error)
        setCodeState(CodeState.ERROR)
      }
    },
    [onResend, countdownDuration],
  )

  // Single guarded submit path shared by auto-submit (handleChange) and the
  // explicit "Suivant" button, so the `submitting` flag gates BOTH and we never
  // fire a second /security_code/verify. The code is verified against the ACTIVE
  // channel so a channel switch stays consistent (fixes the POC switch pitfall).
  const submitCode = useCallback(
    async (code: string) => {
      if (code.length === length && !submitting) {
        setSubmitting(true)
        try {
          await onSubmit(code, activeChannel)
        } finally {
          setSubmitting(false)
        }
      }
    },
    [length, onSubmit, submitting, activeChannel],
  )

  // Auto-submit once the code is complete (mirrors MfaForm UX).
  const handleChange = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      setOtpValue(trimmed)
      submitCode(trimmed)
    },
    [submitCode],
  )

  // Clear the entered code when the parent surfaces an error (e.g. wrong code),
  // so the stale value is removed and auto-submit can fire again on re-entry
  // (mirrors MfaForm's resetForm after a failed attempt).
  useEffect(() => {
    if (errorMessageId) {
      setOtpValue('')
    }
  }, [errorMessageId])

  // Decrement the countdown every second.
  useEffect(() => {
    const interval = setInterval(() => {
      setCountDownValue((c) => Math.max(-1, c - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // When the countdown hits 0, make resend available.
  useEffect(() => {
    if (countDownValue === 0) {
      setCodeState((current) =>
        current === CodeState.RESENT || current === CodeState.SENT ? CodeState.NOT_RECEIVED : current,
      )
    }
  }, [countDownValue])

  // When per-channel targets are provided (MFA), the recipient follows the ACTIVE
  // channel so it updates after a switch; otherwise fall back to the fixed
  // single-channel `maskedRecipient`.
  const recipient =
    (obfuscatedRecipients
      ? activeChannel === SecondaryFactor.SMS
        ? obfuscatedRecipients.phone
        : obfuscatedRecipients.email
      : maskedRecipient) ?? ''

  // "I don't have access to my email" → send an SMS (MFA, email active, SMS
  // available). handleResend switches the active channel, so the message +
  // recipient follow. Falls back to the contact-care escape hatch when SMS is
  // not an option (email-only account) so a genuinely-stuck user isn't trapped.
  const canSwitchToSms = activeChannel === SecondaryFactor.EMAIL && (channels ?? []).includes(SecondaryFactor.SMS)

  const feedback: Record<CodeState, ReactNode> = {
    [CodeState.SENT]: (
      <Typography variant="footnote" style={{ color: theme.colors.content.subdued }} p={4} tabIndex={-1}>
        {formatMessage({ id: 'sign_in.form.mfa.feedback.not_received' })} ({countDownValue})
      </Typography>
    ),
    [CodeState.NOT_RECEIVED]: (
      <Typography
        variant="footnote"
        style={{ color: theme.colors.content.subdued }}
        p={4}
        tabIndex={-1}
        type="reset"
        component="button"
        onClick={() => setCodeState(CodeState.RESEND_AVAILABLE)}
      >
        {formatMessage({ id: 'sign_in.form.mfa.feedback.not_received' })}
      </Typography>
    ),
    [CodeState.RESEND_AVAILABLE]: (
      <div className="flex flex-col">
        {isMultiChannel ? (
          // Multi-channel (MFA): one per-channel resend link, mirroring MfaForm's
          // resend_code_by_email / resend_code_by_sms.
          (channels ?? []).map((ch) => (
            <Typography
              key={ch}
              variant="footnote"
              style={{ color: theme.colors.content.subdued }}
              p={4}
              tabIndex={-1}
              type="reset"
              component="button"
              data-testid={`br-activation-resend-${ch}`}
              onClick={() => handleResend(ch)}
            >
              {formatMessage({ id: `sign_in.form.mfa.resend_code_by_${ch}` })}
            </Typography>
          ))
        ) : (
          <Typography
            variant="footnote"
            style={{ color: theme.colors.content.subdued }}
            p={4}
            tabIndex={-1}
            type="reset"
            component="button"
            onClick={() => handleResend(activeChannel)}
          >
            {formatMessage({ id: 'br_activation.otp.resend' })}
          </Typography>
        )}
      </div>
    ),
    [CodeState.RESENT]: (
      <>
        <Typography variant="footnote" style={{ color: theme.colors.content.subdued }} p={4}>
          {formatMessage({ id: 'sign_in.form.mfa.feedback.code_resent' }, { secondaryFactor: activeChannel })}
        </Typography>
        <Typography variant="footnote" style={{ color: theme.colors.content.subdued }} p={4}>
          {formatMessage({ id: 'sign_in.form.mfa.feedback.not_received' })} ({countDownValue})
        </Typography>
      </>
    ),
    [CodeState.ERROR]: (
      <Typography variant="footnote" style={{ color: theme.colors.content.critical }} p={4}>
        {formatMessage({ id: 'sign_in.form.mfa.feedback.resend_failed' })}
      </Typography>
    ),
  }

  return (
    <ActivationStepLayout
      titleId={titleId}
      errorId={errorId}
      errorMessageId={errorMessageId}
      errorActions={errorActions}
      showBackButton={showBackButton}
      onBack={onBack}
      footer={
        <>
          <Button
            aria-label="submit"
            variant="primary"
            className="w-full"
            type="button"
            disabled={otpValue.length !== length || submitting}
            onClick={() => {
              submitCode(otpValue)
            }}
          >
            {formatMessage({ id: 'sign_in.form.mfa.submit' })}
          </Button>
          {canSwitchToSms && (
            <Button
              variant="alternativeText"
              className="w-full"
              type="button"
              onClick={() => handleResend(SecondaryFactor.SMS)}
            >
              {formatMessage({ id: 'sign_in.form.mfa.no_access_to_email' })}
            </Button>
          )}
        </>
      }
    >
      <Typography variant="body" id={otpDescriptionId}>
        {formatMessage(
          { id: messageId },
          {
            recipient,
            channel: activeChannel,
            strong: (text: ReactNode) => <strong className="contents font-medium">{text}</strong>,
          },
        )}
      </Typography>
      <div data-testid="br-activation-otp">
        <OTPInput
          autoFocus
          length={length}
          value={otpValue}
          onChange={handleChange}
          type={variant}
          aria-labelledby={otpDescriptionId}
          aria-label={formatMessage({ id: 'br_activation.otp.aria_label' })}
        />
      </div>
      <div className="mt-16" aria-live="polite">
        {feedback[codeState]}
      </div>
    </ActivationStepLayout>
  )
}
